/**
 * Synthesized alert tones for system-status alarms.
 *
 * Uses Web Audio API so no binary sound asset is required. Browsers often
 * block audio until a user gesture — call `unlockAlertSound()` from a
 * click/keydown handler (also done from the status-bar mute toggle).
 */
import PB from '@/brokers/PersistencyBroker'
import Constants from '@/helpers/Constants'

export type AlertSoundId = 'klaxon' | 'siren' | 'buzzer' | 'chime'

export const ALERT_SOUND_OPTIONS: Array<{ id: AlertSoundId; label: string }> = [
  { id: 'siren', label: 'Siren' },
  { id: 'klaxon', label: 'Klaxon' },
  { id: 'buzzer', label: 'Buzzer' },
  { id: 'chime', label: 'Chime' },
]

const DEFAULT_SOUND: AlertSoundId = 'siren'
const VALID_IDS = new Set<string>(ALERT_SOUND_OPTIONS.map((o) => o.id))

let audioCtx: AudioContext | null = null
let outputGain: GainNode | null = null
let unlocked = false
/** Bumped by silenceAlertSound() so pending resume()/play callbacks are ignored. */
let playGeneration = 0

function getContext(): AudioContext | null {
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) {
    return null
  }
  if (!audioCtx) {
    audioCtx = new AC()
  }
  return audioCtx
}

function getOutput(ctx: AudioContext): GainNode {
  if (!outputGain || outputGain.context !== ctx) {
    outputGain = ctx.createGain()
    outputGain.gain.value = 1
    outputGain.connect(ctx.destination)
  }
  return outputGain
}

/** Resume the audio context after a user gesture (autoplay policy). */
export function unlockAlertSound(): void {
  const ctx = getContext()
  if (!ctx) {
    return
  }
  if (ctx.state === 'suspended') {
    void ctx.resume().then(() => {
      unlocked = true
    })
  } else {
    unlocked = true
  }
}

/**
 * Immediately silence any in-flight alert and cancel pending play callbacks.
 * Call this when the user mutes status alerts.
 */
export function silenceAlertSound(): void {
  playGeneration += 1
  const ctx = audioCtx
  if (!ctx || !outputGain) {
    return
  }
  const now = ctx.currentTime
  try {
    outputGain.gain.cancelScheduledValues(now)
    outputGain.gain.setValueAtTime(0, now)
  } catch {
    outputGain.gain.value = 0
  }
}

function _restoreOutput(ctx: AudioContext): void {
  const out = getOutput(ctx)
  const now = ctx.currentTime
  try {
    out.gain.cancelScheduledValues(now)
    out.gain.setValueAtTime(1, now)
  } catch {
    out.gain.value = 1
  }
}

export function getAlertSoundId(): AlertSoundId {
  const stored = PB.get(Constants.LOCAL_STORAGE_IDS.STATUS_ALERT_SOUND)
  if (typeof stored === 'string' && VALID_IDS.has(stored)) {
    return stored as AlertSoundId
  }
  return DEFAULT_SOUND
}

export function setAlertSoundId(id: AlertSoundId): void {
  if (!VALID_IDS.has(id)) {
    return
  }
  PB.set(Constants.LOCAL_STORAGE_IDS.STATUS_ALERT_SOUND, id)
}

/**
 * Play the selected (or given) alert sound. Safe to call repeatedly.
 */
export function playAlertSound(soundId?: AlertSoundId): void {
  const ctx = getContext()
  if (!ctx) {
    return
  }
  const id = soundId && VALID_IDS.has(soundId) ? soundId : getAlertSoundId()
  const generation = playGeneration
  const play = () => {
    if (generation !== playGeneration) {
      return
    }
    unlocked = true
    _restoreOutput(ctx)
    switch (id) {
      case 'siren':
        _playSiren(ctx)
        break
      case 'buzzer':
        _playBuzzer(ctx)
        break
      case 'chime':
        _playChime(ctx)
        break
      case 'klaxon':
      default:
        _playKlaxon(ctx)
        break
    }
  }
  if (ctx.state === 'suspended') {
    void ctx.resume().then(play)
    return
  }
  play()
}

export function isAlertSoundUnlocked(): boolean {
  return unlocked
}

function _connect(master: GainNode, ctx: AudioContext): void {
  master.connect(getOutput(ctx))
}

function _playKlaxon(ctx: AudioContext): void {
  const now = ctx.currentTime
  const sweeps = [
    { start: 0.0, from: 420, to: 1400, dur: 0.38 },
    { start: 0.42, from: 420, to: 1400, dur: 0.38 },
    { start: 0.88, from: 900, to: 1600, dur: 0.22 },
  ]
  const peakGain = 0.3

  for (const sweep of sweeps) {
    const master = ctx.createGain()
    master.gain.setValueAtTime(0.0001, now + sweep.start)
    master.gain.exponentialRampToValueAtTime(peakGain, now + sweep.start + 0.02)
    master.gain.setValueAtTime(peakGain, now + sweep.start + sweep.dur - 0.04)
    master.gain.exponentialRampToValueAtTime(0.0001, now + sweep.start + sweep.dur)
    _connect(master, ctx)

    const osc = ctx.createOscillator()
    osc.type = 'square'
    osc.frequency.setValueAtTime(sweep.from, now + sweep.start)
    osc.frequency.exponentialRampToValueAtTime(
      sweep.to,
      now + sweep.start + sweep.dur,
    )
    osc.connect(master)
    osc.start(now + sweep.start)
    osc.stop(now + sweep.start + sweep.dur + 0.02)

    const sub = ctx.createOscillator()
    const subGain = ctx.createGain()
    sub.type = 'sawtooth'
    sub.frequency.setValueAtTime(sweep.from * 0.5, now + sweep.start)
    sub.frequency.exponentialRampToValueAtTime(
      sweep.to * 0.5,
      now + sweep.start + sweep.dur,
    )
    subGain.gain.value = 0.45
    sub.connect(subGain)
    subGain.connect(master)
    sub.start(now + sweep.start)
    sub.stop(now + sweep.start + sweep.dur + 0.02)
  }
}

function _playSiren(ctx: AudioContext): void {
  const now = ctx.currentTime
  const pulses = [
    { freq: 1480, start: 0.0 },
    { freq: 980, start: 0.11 },
    { freq: 1480, start: 0.22 },
    { freq: 980, start: 0.33 },
    { freq: 1480, start: 0.44 },
    { freq: 980, start: 0.55 },
    { freq: 1650, start: 0.7 },
    { freq: 1650, start: 0.9 },
  ]
  const pulseDur = 0.1
  const peakGain = 0.28

  for (const pulse of pulses) {
    const master = ctx.createGain()
    master.gain.setValueAtTime(0.0001, now + pulse.start)
    master.gain.exponentialRampToValueAtTime(peakGain, now + pulse.start + 0.008)
    master.gain.exponentialRampToValueAtTime(0.0001, now + pulse.start + pulseDur)
    _connect(master, ctx)

    for (const [type, detune] of [
      ['square', 0],
      ['sawtooth', 18],
    ] as const) {
      const osc = ctx.createOscillator()
      osc.type = type
      osc.frequency.value = pulse.freq
      osc.detune.value = detune
      osc.connect(master)
      osc.start(now + pulse.start)
      osc.stop(now + pulse.start + pulseDur + 0.02)
    }
  }
}

function _playBuzzer(ctx: AudioContext): void {
  const now = ctx.currentTime
  const peakGain = 0.26
  const hits = [
    { start: 0.0, dur: 0.22 },
    { start: 0.3, dur: 0.22 },
    { start: 0.6, dur: 0.35 },
  ]

  for (const hit of hits) {
    const master = ctx.createGain()
    master.gain.setValueAtTime(0.0001, now + hit.start)
    master.gain.exponentialRampToValueAtTime(peakGain, now + hit.start + 0.01)
    master.gain.exponentialRampToValueAtTime(0.0001, now + hit.start + hit.dur)
    _connect(master, ctx)

    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.value = 180
    osc.connect(master)
    osc.start(now + hit.start)
    osc.stop(now + hit.start + hit.dur + 0.02)

    const hi = ctx.createOscillator()
    const hiGain = ctx.createGain()
    hi.type = 'square'
    hi.frequency.value = 740
    hiGain.gain.value = 0.55
    hi.connect(hiGain)
    hiGain.connect(master)
    hi.start(now + hit.start)
    hi.stop(now + hit.start + hit.dur + 0.02)
  }
}

function _playChime(ctx: AudioContext): void {
  const now = ctx.currentTime
  const notes = [
    { freq: 1046.5, start: 0.0, dur: 0.28 },
    { freq: 784.0, start: 0.22, dur: 0.35 },
    { freq: 1046.5, start: 0.55, dur: 0.4 },
  ]
  const peakGain = 0.22

  for (const note of notes) {
    const master = ctx.createGain()
    master.gain.setValueAtTime(0.0001, now + note.start)
    master.gain.exponentialRampToValueAtTime(peakGain, now + note.start + 0.015)
    master.gain.exponentialRampToValueAtTime(0.0001, now + note.start + note.dur)
    _connect(master, ctx)

    const osc = ctx.createOscillator()
    osc.type = 'triangle'
    osc.frequency.value = note.freq
    osc.connect(master)
    osc.start(now + note.start)
    osc.stop(now + note.start + note.dur + 0.02)
  }
}
